// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductAddToCartButton from "@flip/Overrides/Components/ProductAddToCartButton";
import ProductQuantityOrdered from "@flip/Overrides/Components/ProductQuantityOrdered";
import { mergeWithCss } from "@flip/XDL/Utilities/mergeWithCss";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import * as React from "react";
import { FC, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface ProductActionsProps {
    carouselId?: string;
    showActions?: boolean;
    alwaysShowQty?: boolean;
    extendedStyles?: ProductActionsStyle;
    onAddToCartClick?: () => void;
    currentPage?: string;
}

type Props = ProductActionsProps & HasProductContext;

export interface ProductActionsStyle {
    addToCartContainer?: GridContainerProps;
    qtyWrapper?: GridItemProps;
    addToCartButtonWrapper?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
}

const productActionsStyle: ProductActionsStyle = {
    addToCartContainer: {
        css: css`
            display: flex;
            align-items: center;
            width: 100%;
        `,
    },
    qtyWrapper: {
        css: css`
            margin-right: 10px;
        `,
    },

    productAddToCartButtonStyles: {
        mergeCss: true,
        css: css`
            min-height: 30px;
            width: 100%;
        `,
    },
};

const mapStateToProps = (state: ApplicationState) => ({
    currentPage: getCurrentPage(state)?.type,
});
const ProductActions: FC<Props> = ({
    carouselId,
    extendedStyles,
    showActions = true,
    alwaysShowQty,
    onAddToCartClick,
    currentPage,
}) => {
    const [styles] = useState(() => mergeWithCss(productActionsStyle, extendedStyles));
    return (
        <StyledWrapper {...styles.addToCartContainer} data-test-selector="product_actions">
            <StyledWrapper {...styles.qtyWrapper}>
                <VisuallyHidden as="label" htmlFor={`${carouselId}-qty`} id={`${carouselId}-qty-label`}>
                    {translate("QTY")}
                </VisuallyHidden>
                <ProductQuantityOrdered
                    labelOverride=""
                    data-test-selector="productQty"
                />
            </StyledWrapper>

            {currentPage === "ProductDetailsPage" &&
                createWidgetElement("ProductDetails/ProductDetailsUnitOfMeasureSelect", {
                    fields: {},
                })}
            <StyledWrapper {...styles.addToCartButtonWrapper}>
                <ProductAddToCartButton
                    onAddToCartClick={onAddToCartClick}
                    extendedStyles={styles.productAddToCartButtonStyles}
                    data-test-selector="addToCartBtn"
                />
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductActions));
